

import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import Login from "./pages/Login";
import PrivateRoute from "./components/PrivateRoute";
import Navbar from "./components/Navbar";
import PageMeta from "./components/PageMeta";
import NewEmployeeManual from "./components/NewEmployeeManual";
import EmailMarketingPortal from "./components/EmailMarketingPortal";
import WhatsappMarketingPortal from "./components/WhatsappMarketingPortal";
import EmployeeList from "./pages/EmployeeList";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import ManageAccess from "./pages/ManageAccess";
import OfferLetterEditor from "./pages/OfferLetterEditor";
import IDCardGenerator from "./pages/IDCardGenerator";
import HRTools from "./pages/HRTools";
import ChecklistTemplateManager from "./pages/ChecklistTemplateManager";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MeetingsProvider } from "./context/MeetingsContext";
import { ActiveMeetingProvider } from "./context/ActiveMeetingContext";
import DashboardLayout from "./pages/DashboardLayout";
import PersonalDashboard from "./pages/PersonalDashboard";
import AdminPortal from "./pages/AdminPortal";
import ExperienceRecords from "./pages/ExperienceRecords";
import TaskTicket from "./pages/TaskTicket";
import AboutPrrepX from "./pages/AboutPrrepX";
import PrrepXMeet from "./pages/PrrepXMeet";
import WeeklyPerformance from "./pages/WeeklyPerformance";
import CalendarPage from "./pages/CalendarPage";
import SlideCreator from "./pages/SlideCreator";
import "./styles/global.css";

// Single source of truth for role-based default landing page
const RoleBasedRedirect = () => {
  const { userData } = useAuth();
  if (!userData) return null;
  if (userData.role === 'superadmin' || userData.role === 'admin') {
    return <Navigate to="/dashboard/manage" replace />;
  }
  return <Navigate to="/dashboard/my-portal" replace />;
};

function App() {
  return (
    <AuthProvider>
      <MeetingsProvider>
      <ActiveMeetingProvider>
      <Router>
        <PageMeta />
        <Toaster position="top-center" reverseOrder={false} />
        <Navbar />
        <Routes>
          {/* Default route redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />

          {/* --- Main Dashboard Layout --- */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            {/* Default dashboard landing — role-aware redirect */}
            <Route index element={<RoleBasedRedirect />} />

            {/* Personal Dashboard — employee & admin */}
            <Route
              path="my-portal"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin']}>
                  <PersonalDashboard />
                </PrivateRoute>
              }
            />

            {/* "Management" for admins and superadmins */}
            <Route
              path="manage"
              element={
                <PrivateRoute allowedRoles={['admin', 'superadmin']}>
                  <AdminPortal />
                </PrivateRoute>
              }
            />
            {/* "Experience Records" for admins and superadmins */}
            <Route
              path="experience-records"
              element={
                <PrivateRoute allowedRoles={['admin', 'superadmin']}>
                  <ExperienceRecords />
                </PrivateRoute>
              }
            />
            
            {/* Employee Manual - Accessible by all roles */}
            <Route
              path="employee-manual"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <NewEmployeeManual />
                </PrivateRoute>
              }
            />

            {/* Slide Creator - Accessible by all roles */}
            <Route
              path="slide-creator"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <SlideCreator />
                </PrivateRoute>
              }
            />

            {/* About PrrepX Pathshala - Accessible by all roles */}
            <Route
              path="about"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <AboutPrrepX />
                </PrivateRoute>
              }
            />

            {/* --- ✨ 3. NEW SEPARATE ROUTES --- */}
            {/* Email Portal - Accessible by superadmin only */}
            <Route
              path="email-portal"
              element={
                <PrivateRoute allowedRoles={['superadmin']}>
                  <EmailMarketingPortal />
                </PrivateRoute>
              }
            />
            {/* WhatsApp Portal - Accessible by superadmin only */}
            <Route
              path="whatsapp-portal"
              element={
                <PrivateRoute allowedRoles={['superadmin']}>
                  <WhatsappMarketingPortal />
                </PrivateRoute>
              }
            />

            {/* Employee List - Custom access control (authorized_viewers collection) */}
            <Route
              path="employee-list"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <EmployeeList />
                </PrivateRoute>
              }
            />


            {/* Employee Detail Page - Full page view for employee details */}
            <Route
              path="employee-detail/:employeeId"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <EmployeeDetailPage />
                </PrivateRoute>
              }
            />

            {/* Task Ticket Page - Dedicated view for a single task (ticketNumber-based URL) */}
            <Route
              path="task/:taskId"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <TaskTicket />
                </PrivateRoute>
              }
            />

            {/* Legacy redirects — old URLs still work */}
            <Route path="manage-viewers" element={<Navigate to="/dashboard/manage-access" replace />} />
            <Route path="manage-offer-access" element={<Navigate to="/dashboard/manage-access" replace />} />
            <Route path="hr-tools" element={<Navigate to="/dashboard/prrepx-standard" replace />} />

            {/* Unified Access Management - Superadmin only */}
            <Route
              path="manage-access"
              element={
                <PrivateRoute allowedRoles={['superadmin']}>
                  <ManageAccess />
                </PrivateRoute>
              }
            />

            {/* Offer Letter Editor - Superadmin + authorized admins (access check inside component) */}
            <Route
              path="offer-letter"
              element={
                <PrivateRoute allowedRoles={['admin', 'superadmin']}>
                  <OfferLetterEditor />
                </PrivateRoute>
              }
            />

            {/* ID Card Generator - Superadmin + authorized admins (access check inside component) */}
            <Route
              path="id-card"
              element={
                <PrivateRoute allowedRoles={['admin', 'superadmin']}>
                  <IDCardGenerator />
                </PrivateRoute>
              }
            />

            {/* HR Tools - Superadmin + authorized admins (access check inside component) */}
            <Route
              path="prrepx-standard"
              element={
                <PrivateRoute allowedRoles={['admin', 'superadmin']}>
                  <HRTools />
                </PrivateRoute>
              }
            />

            {/* Checklist Template Manager - Superadmin + users with checklistAccess */}
            <Route
              path="checklist-manager"
              element={
                <PrivateRoute allowedRoles={['admin', 'superadmin']}>
                  <ChecklistTemplateManager />
                </PrivateRoute>
              }
            />

            {/* Team Calendar - Accessible by all roles */}
            <Route
              path="calendar"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <CalendarPage />
                </PrivateRoute>
              }
            />

            {/* PrrepX Meet - Accessible by all roles */}
            <Route
              path="meet"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <PrrepXMeet />
                </PrivateRoute>
              }
            />

            {/* Weekly Progress Reporting - Accessible by all roles */}
            <Route
              path="weekly-progress"
              element={
                <PrivateRoute allowedRoles={['employee', 'admin', 'superadmin']}>
                  <WeeklyPerformance />
                </PrivateRoute>
              }
            />

          </Route>

          {/* Fallback route for any other path */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
      </ActiveMeetingProvider>
      </MeetingsProvider>
    </AuthProvider>
  );
}

export default App;